<script setup>
import { useApi } from '@/utils/api'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Testimonial',
  },
})

const form = ref({
  rating: 0,
  message: '',
})

const formRef = ref()
const saving = ref(false)
const errors = ref({})
const generalError = ref('')
const submitted = ref(false)

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid || !form.value.rating)
    return

  saving.value = true
  errors.value = {}
  generalError.value = ''

  try {
    await useApi('/api/v1/testimonials').post({ body: form.value })

    submitted.value = true
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      generalError.value = data?.message ?? 'Gagal mengirim testimoni. Silakan coba lagi.'
  } finally {
    saving.value = false
  }
}

const submitAnother = () => {
  form.value = { rating: 0, message: '' }
  submitted.value = false
}
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-bold">
          Beri Testimoni
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Ceritakan pengalaman Anda memakai PintuKasir — testimoni Anda akan ditinjau dulu
          sebelum tampil di halaman publik
        </p>
      </VCol>
    </VRow>

    <VRow justify="center">
      <VCol
        cols="12"
        md="7"
        lg="6"
      >
        <VCard>
          <VCardText
            v-if="submitted"
            class="pa-8 text-center"
          >
            <VIcon
              icon="ri-checkbox-circle-fill"
              size="56"
              color="success"
              class="mb-4"
            />
            <h5 class="text-h5 font-weight-bold mb-2">
              Terima Kasih!
            </h5>
            <p class="text-body-2 text-medium-emphasis mb-6">
              Testimoni Anda sudah kami terima dan sedang ditinjau oleh tim PintuKasir.
              Testimoni akan tampil di halaman publik setelah disetujui.
            </p>
            <VBtn
              variant="tonal"
              @click="submitAnother"
            >
              Kirim Testimoni Lain
            </VBtn>
          </VCardText>

          <VCardText
            v-else
            class="pa-6"
          >
            <VAlert
              v-if="generalError"
              type="error"
              variant="tonal"
              class="mb-4"
              closable
              @click:close="generalError = ''"
            >
              {{ generalError }}
            </VAlert>

            <VForm
              ref="formRef"
              @submit.prevent="submit"
            >
              <div class="text-center mb-6">
                <p class="text-body-2 text-medium-emphasis mb-2">
                  Berapa bintang untuk PintuKasir?
                </p>
                <VRating
                  v-model="form.rating"
                  color="warning"
                  active-color="warning"
                  size="40"
                  hover
                />
                <p
                  v-if="errors.rating"
                  class="text-error text-caption mt-1 mb-0"
                >
                  {{ errors.rating[0] }}
                </p>
              </div>

              <VTextarea
                v-model="form.message"
                label="Ulasan Anda *"
                placeholder="Apa yang Anda suka dari PintuKasir? Bagaimana pengalaman Anda menggunakannya sehari-hari?"
                rows="5"
                :rules="[requiredValidator]"
                :error-messages="errors.message"
              />
            </VForm>
          </VCardText>

          <template v-if="!submitted">
            <VDivider />
            <VCardActions class="pa-6">
              <VSpacer />
              <VBtn
                color="primary"
                :loading="saving"
                :disabled="!form.rating"
                @click="submit"
              >
                Kirim Testimoni
              </VBtn>
            </VCardActions>
          </template>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
